/* eslint-disable complexity */
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';
import { LiveReportList } from '@futrli-sdk/predict';
import { WorkspaceMember } from '@futrli-sdk/people';

import { getUserName } from '../../utils/users/getUserName';

export interface ReportActions {
  addToTemplates: boolean;
  createSchedule: boolean;
  delete: boolean;
  duplicate: boolean;
  edit: boolean;
  emailPdf: boolean;
  exportPdf: boolean;
  lock: boolean;
  publish: boolean;
  share: boolean;
  unpublish: boolean;
  unlock: boolean;
  view: boolean;
  viewPdf: boolean;
}

export interface ExtendedReportList extends LiveReportList {
  actions: ReportActions;
  isOwner: boolean;
  ownerName?: string;
}

const isResourceSharingEnabled = (flags: LDFlagSet): boolean => Boolean(flags.resourceSharing);

export const processReport = (
  report: LiveReportList,
  currentUser: CurrentUserCheck,
  members: WorkspaceMember[],
  flags: LDFlagSet = {}
): ExtendedReportList => {
  const hasResourceSharingEnabled = isResourceSharingEnabled(flags);
  const permissionInfo = hasResourceSharingEnabled ? report.permission_info : undefined;
  const isLocked = report.is_locked;
  const ownerId = hasResourceSharingEnabled ? report.permission_info.owner_id : report.owner_id;
  const isOwner = ownerId === currentUser.id;
  const isPublished = [report.status === 'live', !isLocked].every(Boolean);
  const isDraft = report.status !== 'live';
  const effectivePermission = permissionInfo?.effective_permission;
  const hasViewPermission =
    hasResourceSharingEnabled ? ['view', 'edit'].includes(effectivePermission ?? '') : true;
  const hasEditPermission = hasResourceSharingEnabled ? effectivePermission === 'edit' : true;
  const canDelete = [hasEditPermission, isOwner ? true : !isLocked].every(Boolean);
  const canEdit = [hasEditPermission, !isLocked].every(Boolean);
  const canExport = [hasViewPermission, !isDraft].every(Boolean);
  const canLock = [!hasResourceSharingEnabled, hasEditPermission, isOwner, isPublished].every(Boolean);
  const canShare = [hasResourceSharingEnabled, hasEditPermission].every(Boolean);
  const canUnlock = [!hasResourceSharingEnabled, hasEditPermission, isOwner, isLocked].every(Boolean);
  const owner = members.find(member => member.id === ownerId);
  const ownerName = owner ? getUserName(owner.name) : undefined;

  return {
    ...report,
    actions: {
      addToTemplates: hasViewPermission,
      createSchedule: hasViewPermission,
      delete: canDelete,
      duplicate: hasViewPermission,
      edit: canEdit,
      emailPdf: hasViewPermission,
      exportPdf: canExport,
      lock: canLock,
      publish: !hasResourceSharingEnabled && isDraft,
      share: canShare,
      unpublish: !hasResourceSharingEnabled && isPublished,
      unlock: canUnlock,
      view: hasViewPermission,
      viewPdf: hasViewPermission
    },
    isOwner,
    ownerName
  };
};

export const processAllReports = (
  reports: LiveReportList[],
  currentUser: CurrentUserCheck,
  members: WorkspaceMember[],
  flags: LDFlagSet = {}
): ExtendedReportList[] => reports.map(report => processReport(report, currentUser, members, flags));

export const processReports = (
  reports: LiveReportList[],
  currentUser: CurrentUserCheck,
  members: WorkspaceMember[],
  flags: LDFlagSet = {}
): ExtendedReportList[] =>
  processAllReports(reports, currentUser, members, flags).filter(report => report.orientation !== 'dashboard');
